/**
 * Transforms Quill editor HTML into standard HTML for rendering outside the Quill editor.
 *
 * Quill outputs non-standard markup:
 * - All lists use <ol>, with <li data-list="bullet"> for unordered and <li data-list="ordered"> for ordered
 * - Indentation uses class="ql-indent-N" (N = 1-8)
 * - Editor UI spans: <span class="ql-ui" contenteditable="false"></span>
 *
 * This function normalises that into standard HTML before DOMPurify sanitisation.
 */
export function transformQuillHtml(html) {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // 1. Remove Quill UI spans (<span class="ql-ui" contenteditable="false">)
  doc.querySelectorAll('span.ql-ui').forEach(span => span.remove());

  // 2. Convert ql-indent-N classes to margin-left inline styles
  for (let level = 8; level >= 1; level--) {
    doc.querySelectorAll(`.ql-indent-${level}`).forEach(el => {
      el.classList.remove(`ql-indent-${level}`);
      const px = level * 2; // 2em per indent level (matches Quill's default)
      const existing = el.getAttribute('style') || '';
      el.setAttribute('style', `${existing}${existing ? '; ' : ''}margin-left: ${px}em`);
    });
  }

  // 3. Convert <ol> containing bullet list items to <ul>
  doc.querySelectorAll('ol').forEach(ol => {
    const items = ol.querySelectorAll(':scope > li');
    if (items.length === 0) return;

    // Check if ALL items are bullet type
    const allBullet = Array.from(items).every(li => li.getAttribute('data-list') === 'bullet');
    if (allBullet) {
      // Replace <ol> with <ul>
      const ul = doc.createElement('ul');
      // Copy attributes (except type-specific ones)
      for (const attr of ol.attributes) {
        ul.setAttribute(attr.name, attr.value);
      }
      while (ol.firstChild) {
        ul.appendChild(ol.firstChild);
      }
      ol.parentNode.replaceChild(ul, ol);
    }
    // If mixed or ordered, leave as <ol>

    // Clean up data-list attributes from all <li> elements
    (allBullet ? doc.querySelectorAll('ul > li') : items).forEach(li => {
      li.removeAttribute('data-list');
    });
  });

  // Also clean data-list from any remaining <li> elements
  doc.querySelectorAll('li[data-list]').forEach(li => {
    li.removeAttribute('data-list');
  });

  return doc.body.innerHTML;
}
